import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { 
  Eye, Star, TrendingUp, TrendingDown, AlertTriangle, Target, 
  DollarSign, BarChart3, PieChart, Image, Video, FileText, Zap, 
  Download, Search, Filter, RefreshCw, Play, Pause, ThumbsUp, 
  ThumbsDown, Clock, Users, MousePointer, Heart, MessageSquare
} from 'lucide-react';
import AIInsightsChatbot from '../components/Dashboard/AIInsightsChatbot';

const CreativeAudit = () => {
  const [selectedChannel, setSelectedChannel] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('last_30_days');
  const [sortBy, setSortBy] = useState('performance');
  const [filterType, setFilterType] = useState('all');

  // Creative performance data
  const creativeData = [
    {
      id: 1,
      name: 'Ramadan Special Offer',
      type: 'video',
      channel: 'Digital',
      campaign: 'Seasonal Q2',
      thumbnail: '/api/placeholder/300/200',
      metrics: {
        impressions: 2450000,
        clicks: 89500,
        ctr: 3.65,
        conversions: 1200,
        cvr: 1.34,
        cpa: 45.80,
        roas: 4.2,
        engagement: 8.7,
        reach: 890000,
        frequency: 2.8
      },
      performance: 'excellent',
      sentimentScore: 0.87,
      brandSafety: 'high',
      createdDate: '2025-03-01',
      lastUpdated: '2025-03-15',
      status: 'active'
    },
    {
      id: 2,
      name: 'UAE National Day Pride',
      type: 'image',
      channel: 'Outdoor',
      campaign: 'National Events',
      thumbnail: '/api/placeholder/300/200',
      metrics: {
        impressions: 1800000,
        clicks: 45200,
        ctr: 2.51,
        conversions: 680,
        cvr: 1.50,
        cpa: 52.30,
        roas: 3.8,
        engagement: 6.2,
        reach: 650000,
        frequency: 2.8
      },
      performance: 'good',
      sentimentScore: 0.92,
      brandSafety: 'high',
      createdDate: '2024-11-15',
      lastUpdated: '2024-12-02',
      status: 'completed'
    },
    {
      id: 3,
      name: 'Flash Sale Summer',
      type: 'carousel',
      channel: 'Digital',
      campaign: 'Promotional',
      thumbnail: '/api/placeholder/300/200',
      metrics: {
        impressions: 3200000,
        clicks: 128000,
        ctr: 4.00,
        conversions: 2100,
        cvr: 1.64,
        cpa: 38.10,
        roas: 5.1,
        engagement: 12.3,
        reach: 1200000,
        frequency: 2.7
      },
      performance: 'excellent',
      sentimentScore: 0.75,
      brandSafety: 'medium',
      createdDate: '2025-07-01',
      lastUpdated: '2025-07-31',
      status: 'paused'
    },
    {
      id: 4,
      name: 'Business Solutions',
      type: 'video',
      channel: 'TV',
      campaign: 'B2B Outreach',
      thumbnail: '/api/placeholder/300/200',
      metrics: {
        impressions: 890000,
        clicks: 15600,
        ctr: 1.75,
        conversions: 180,
        cvr: 1.15,
        cpa: 125.50,
        roas: 2.3,
        engagement: 4.1,
        reach: 420000,
        frequency: 2.1
      },
      performance: 'needs_improvement',
      sentimentScore: 0.68,
      brandSafety: 'high',
      createdDate: '2025-02-10',
      lastUpdated: '2025-03-01',
      status: 'active'
    }
  ];

  // Performance benchmarks
  const benchmarks = {
    'Digital': { ctr: 2.8, cvr: 1.2, roas: 3.5, engagement: 7.2 },
    'TV': { ctr: 1.2, cvr: 0.8, roas: 2.8, engagement: 4.5 },
    'Outdoor': { ctr: 1.8, cvr: 1.0, roas: 3.2, engagement: 5.8 },
    'Radio': { ctr: 1.5, cvr: 0.9, roas: 2.5, engagement: 3.8 }
  };

  // Creative insights and recommendations
  const insights = [
    {
      id: 1,
      type: 'opportunity',
      title: 'Video Content Outperforming',
      description: 'Video creatives show 34% higher engagement rates compared to static images',
      impact: 'High',
      recommendation: 'Increase video content allocation by 25% for Q2 campaigns'
    },
    {
      id: 2,
      type: 'warning',
      title: 'Creative Fatigue Detected',
      description: 'Ramadan campaign showing declining CTR after 3+ exposures',
      impact: 'Medium',
      recommendation: 'Refresh creative variants or implement frequency capping'
    },
    {
      id: 3,
      type: 'success',
      title: 'Carousel Format Success',
      description: 'Multi-product carousels driving 45% higher ROAS in digital channels',
      impact: 'High',
      recommendation: 'Scale carousel format across similar product categories'
    }
  ];

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getPerformanceBadge = (performance) => {
    switch(performance) {
      case 'excellent': return 'bg-green-100 text-green-800';
      case 'good': return 'bg-blue-100 text-blue-800';
      case 'average': return 'bg-yellow-100 text-yellow-800';
      case 'needs_improvement': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCreativeTypeIcon = (type) => {
    switch(type) {
      case 'video': return <Video className="h-4 w-4" />;
      case 'image': return <Image className="h-4 w-4" />;
      case 'carousel': return <BarChart3 className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getInsightIcon = (type) => {
    switch(type) {
      case 'opportunity': return <TrendingUp className="h-5 w-5 text-green-600" />;
      case 'warning': return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      case 'success': return <Target className="h-5 w-5 text-blue-600" />;
      default: return <Eye className="h-5 w-5 text-gray-600" />;
    }
  };

  const filteredCreatives = creativeData.filter(creative => {
    const channelMatch = selectedChannel === 'all' || creative.channel === selectedChannel;
    const typeMatch = filterType === 'all' || creative.type === filterType;
    return channelMatch && typeMatch;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-2">
            Creative Audit & Performance Analysis
          </h1>
          <p className="text-gray-600">Comprehensive creative performance analysis with AI-powered insights and optimization recommendations</p>
        </div>

        {/* Controls */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Filter className="h-5 w-5 mr-2 text-purple-600" />
              Analysis Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Select value={selectedChannel} onValueChange={setSelectedChannel}>
                <SelectTrigger>
                  <SelectValue placeholder="All Channels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Channels</SelectItem>
                  <SelectItem value="Digital">Digital</SelectItem>
                  <SelectItem value="TV">TV</SelectItem>
                  <SelectItem value="Outdoor">Outdoor</SelectItem>
                  <SelectItem value="Radio">Radio</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger>
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_7_days">Last 7 Days</SelectItem>
                  <SelectItem value="last_30_days">Last 30 Days</SelectItem>
                  <SelectItem value="last_90_days">Last 90 Days</SelectItem>
                  <SelectItem value="ytd">Year to Date</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger>
                  <SelectValue placeholder="Creative Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="image">Image</SelectItem>
                  <SelectItem value="carousel">Carousel</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="performance">Performance</SelectItem>
                  <SelectItem value="roas">ROAS</SelectItem>
                  <SelectItem value="engagement">Engagement</SelectItem>
                  <SelectItem value="date">Date Created</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Performance Overview</TabsTrigger>
            <TabsTrigger value="creatives">Creative Analysis</TabsTrigger>
            <TabsTrigger value="insights">AI Insights</TabsTrigger>
            <TabsTrigger value="optimization">Optimization</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Performance Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Creatives</p>
                      <p className="text-2xl font-bold text-purple-600">247</p>
                      <p className="text-xs text-green-600">+12 this month</p>
                    </div>
                    <Eye className="h-8 w-8 text-purple-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Avg ROAS</p>
                      <p className="text-2xl font-bold text-green-600">3.8x</p>
                      <p className="text-xs text-green-600">+15% vs benchmark</p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Top Performer</p>
                      <p className="text-2xl font-bold text-blue-600">5.1x</p>
                      <p className="text-xs text-blue-600">Flash Sale Summer</p>
                    </div>
                    <Star className="h-8 w-8 text-yellow-500" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Needs Review</p>
                      <p className="text-2xl font-bold text-red-600">8</p>
                      <p className="text-xs text-red-600">Below benchmark</p>
                    </div>
                    <AlertTriangle className="h-8 w-8 text-red-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Channel Performance Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-purple-600" />
                  Channel Performance Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {Object.entries(benchmarks).map(([channel, metrics]) => (
                    <div key={channel} className="p-4 border rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-3">{channel}</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">CTR:</span>
                          <span className="font-semibold">{metrics.ctr}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">CVR:</span>
                          <span className="font-semibold">{metrics.cvr}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">ROAS:</span>
                          <span className="font-semibold text-green-600">{metrics.roas}x</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Engagement:</span>
                          <span className="font-semibold">{metrics.engagement}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Real-time Performance Tracking */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-green-600" />
                  Real-time Performance Tracking
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-green-900">Live Campaigns</span>
                      <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                    </div>
                    <div className="text-2xl font-bold text-green-600 mb-1">14</div>
                    <div className="text-sm text-green-700">
                      Impressions: 1.2M (last hour)
                    </div>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-blue-900">Engagement Rate</span>
                      <TrendingUp className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="text-2xl font-bold text-blue-600 mb-1">8.7%</div>
                    <div className="text-sm text-blue-700">
                      +12% vs yesterday
                    </div>
                  </div>

                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-purple-900">Revenue Impact</span>
                      <DollarSign className="h-4 w-4 text-purple-600" />
                    </div>
                    <div className="text-2xl font-bold text-purple-600 mb-1">$89K</div>
                    <div className="text-sm text-purple-700">
                      Generated today
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="creatives" className="space-y-6">
            {/* Creative Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCreatives.map((creative) => (
                <Card key={creative.id} className="hover:shadow-lg transition-shadow">
                  <div className="relative">
                    <div className="h-48 bg-gradient-to-br from-gray-200 to-gray-300 rounded-t-lg flex items-center justify-center">
                      <div className="text-center">
                        {getCreativeTypeIcon(creative.type)}
                        <div className="text-sm text-gray-600 mt-2">{creative.name}</div>
                      </div>
                    </div>
                    <div className="absolute top-2 right-2">
                      <Badge className={getPerformanceBadge(creative.performance)}>
                        {creative.performance.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="absolute top-2 left-2">
                      <Badge className="bg-white text-gray-800 text-xs">
                        {creative.channel}
                      </Badge>
                    </div>
                  </div>

                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{creative.name}</CardTitle>
                        <div className="flex items-center mt-1 text-sm text-gray-600">
                          {getCreativeTypeIcon(creative.type)}
                          <span className="ml-1">{creative.type}</span>
                          <span className="mx-2">•</span>
                          <span>{creative.campaign}</span>
                        </div>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                      <div className="text-center p-2 bg-blue-50 rounded">
                        <div className="font-semibold">{formatNumber(creative.metrics.impressions)}</div>
                        <div className="text-xs text-gray-600">Impressions</div>
                      </div>
                      <div className="text-center p-2 bg-green-50 rounded">
                        <div className="font-semibold">{creative.metrics.ctr}%</div>
                        <div className="text-xs text-gray-600">CTR</div>
                      </div>
                      <div className="text-center p-2 bg-purple-50 rounded">
                        <div className="font-semibold">{creative.metrics.roas}x</div>
                        <div className="text-xs text-gray-600">ROAS</div>
                      </div>
                      <div className="text-center p-2 bg-orange-50 rounded">
                        <div className="font-semibold">{formatCurrency(creative.metrics.cpa)}</div>
                        <div className="text-xs text-gray-600">CPA</div>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm mb-4">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Conversions:</span>
                        <span className="font-semibold">{creative.metrics.conversions.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Engagement:</span>
                        <span className="font-semibold">{creative.metrics.engagement}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Reach:</span>
                        <span className="font-semibold">{formatNumber(creative.metrics.reach)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Sentiment:</span>
                        <div className="flex items-center">
                          <span className="font-semibold mr-2">{(creative.sentimentScore * 100).toFixed(0)}%</span>
                          {creative.sentimentScore > 0.8 ? 
                            <ThumbsUp className="h-3 w-3 text-green-600" /> : 
                            creative.sentimentScore > 0.6 ? 
                            <Heart className="h-3 w-3 text-yellow-600" /> : 
                            <ThumbsDown className="h-3 w-3 text-red-600" />
                          }
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" className="flex-1">
                        <Eye className="h-4 w-4 mr-1" />
                        View Details
                      </Button>
                      <Button variant="outline" size="sm" className="flex-1">
                        <Download className="h-4 w-4 mr-1" />
                        Export
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Granular Performance Analysis */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-purple-600" />
                  Granular Performance Analysis (Campaign/Tactic/Ad Set Level)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Creative</th>
                        <th className="text-right p-2">Ad Set</th>
                        <th className="text-right p-2">Impressions</th>
                        <th className="text-right p-2">CTR</th>
                        <th className="text-right p-2">CVR</th>
                        <th className="text-right p-2">CPA</th>
                        <th className="text-right p-2">ROAS</th>
                        <th className="text-right p-2">Spend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCreatives.slice(0, 10).map((creative) => (
                        <tr key={creative.id} className="border-b hover:bg-gray-50">
                          <td className="p-2">
                            <div className="font-medium">{creative.name}</div>
                            <div className="text-xs text-gray-600">{creative.campaign}</div>
                          </td>
                          <td className="text-right p-2">AS-{creative.id.toString().padStart(3, '0')}</td>
                          <td className="text-right p-2">{formatNumber(creative.metrics.impressions)}</td>
                          <td className="text-right p-2">{creative.metrics.ctr}%</td>
                          <td className="text-right p-2">{creative.metrics.cvr}%</td>
                          <td className="text-right p-2">{formatCurrency(creative.metrics.cpa)}</td>
                          <td className="text-right p-2 font-semibold text-green-600">{creative.metrics.roas}x</td>
                          <td className="text-right p-2">{formatCurrency(creative.metrics.cpa * creative.metrics.conversions)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6">
            {/* AI-Powered Insights */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {insights.map((insight) => (
                <Card key={insight.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start space-x-3">
                      {getInsightIcon(insight.type)}
                      <div className="flex-1">
                        <CardTitle className="text-lg">{insight.title}</CardTitle>
                        <Badge className={`mt-1 ${
                          insight.impact === 'High' ? 'bg-red-100 text-red-800' :
                          insight.impact === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {insight.impact} Impact
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4">{insight.description}</p>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="text-sm font-medium text-blue-900 mb-1">Recommendation:</div>
                      <div className="text-sm text-blue-800">{insight.recommendation}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Trend Analysis */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
                  Temporal Insights & Performance Evolution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Performance Trends (Last 90 Days)</h4>
                    <div className="space-y-3">
                      <div className="p-3 bg-green-50 rounded-lg">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium text-green-900">Video Content</span>
                          <TrendingUp className="h-4 w-4 text-green-600" />
                        </div>
                        <div className="text-sm text-green-700">
                          CTR improved by 28% • ROAS increased by 34%
                        </div>
                      </div>

                      <div className="p-3 bg-yellow-50 rounded-lg">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium text-yellow-900">Image Carousel</span>
                          <TrendingDown className="h-4 w-4 text-yellow-600" />
                        </div>
                        <div className="text-sm text-yellow-700">
                          Engagement declining • Refresh needed
                        </div>
                      </div>

                      <div className="p-3 bg-blue-50 rounded-lg">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium text-blue-900">Static Images</span>
                          <RefreshCw className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="text-sm text-blue-700">
                          Stable performance • Seasonal variations observed
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Creative Fatigue Analysis</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-2 border rounded">
                        <span className="font-medium">Ramadan Special Offer</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-16 bg-red-200 rounded-full h-2">
                            <div className="w-3/4 bg-red-500 h-2 rounded-full"></div>
                          </div>
                          <span className="text-sm text-red-600">75%</span>
                        </div>
                      </div>

                      <div className="flex justify-between items-center p-2 border rounded">
                        <span className="font-medium">UAE National Day</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-16 bg-green-200 rounded-full h-2">
                            <div className="w-1/4 bg-green-500 h-2 rounded-full"></div>
                          </div>
                          <span className="text-sm text-green-600">25%</span>
                        </div>
                      </div>

                      <div className="flex justify-between items-center p-2 border rounded">
                        <span className="font-medium">Flash Sale Summer</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-16 bg-yellow-200 rounded-full h-2">
                            <div className="w-1/2 bg-yellow-500 h-2 rounded-full"></div>
                          </div>
                          <span className="text-sm text-yellow-600">50%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Diagnostic Insights */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-yellow-600" />
                  Diagnostic Insights & Anomaly Detection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Performance Anomalies</h4>
                    <div className="space-y-2">
                      <div className="p-2 border-l-4 border-l-red-500 bg-red-50">
                        <div className="font-medium text-red-900">B2B Campaign Underperforming</div>
                        <div className="text-sm text-red-700">CTR 40% below channel benchmark</div>
                      </div>
                      <div className="p-2 border-l-4 border-l-yellow-500 bg-yellow-50">
                        <div className="font-medium text-yellow-900">Weekend Performance Drop</div>
                        <div className="text-sm text-yellow-700">15% decline in weekend engagement</div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Optimization Opportunities</h4>
                    <div className="space-y-2">
                      <div className="p-2 border-l-4 border-l-green-500 bg-green-50">
                        <div className="font-medium text-green-900">Video Format Scaling</div>
                        <div className="text-sm text-green-700">Potential 25% ROAS improvement</div>
                      </div>
                      <div className="p-2 border-l-4 border-l-blue-500 bg-blue-50">
                        <div className="font-medium text-blue-900">Audience Expansion</div>
                        <div className="text-sm text-blue-700">Similar audiences showing high affinity</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="optimization" className="space-y-6">
            {/* Optimization Recommendations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Target className="h-5 w-5 mr-2 text-blue-600" />
                  Creative Optimization Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">High Priority Actions</h4>
                    
                    <div className="p-4 border rounded-lg bg-red-50 border-red-200">
                      <div className="flex items-center mb-2">
                        <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                        <span className="font-medium text-red-900">Critical: Creative Refresh Required</span>
                      </div>
                      <div className="text-sm text-red-700 mb-2">
                        3 campaigns showing severe fatigue with 50%+ CTR decline
                      </div>
                      <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white">
                        Refresh Now
                      </Button>
                    </div>

                    <div className="p-4 border rounded-lg bg-orange-50 border-orange-200">
                      <div className="flex items-center mb-2">
                        <TrendingUp className="h-4 w-4 text-orange-600 mr-2" />
                        <span className="font-medium text-orange-900">Scale Top Performers</span>
                      </div>
                      <div className="text-sm text-orange-700 mb-2">
                        Video carousel format showing 45% higher ROAS
                      </div>
                      <Button size="sm" variant="outline" className="border-orange-600 text-orange-600">
                        Scale Campaign
                      </Button>
                    </div>

                    <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                      <div className="flex items-center mb-2">
                        <Target className="h-4 w-4 text-blue-600 mr-2" />
                        <span className="font-medium text-blue-900">Audience Optimization</span>
                      </div>
                      <div className="text-sm text-blue-700 mb-2">
                        Expand to similar audiences for Flash Sale campaign
                      </div>
                      <Button size="sm" variant="outline" className="border-blue-600 text-blue-600">
                        Expand Audience
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">Budget Reallocation Suggestions</h4>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                        <span className="font-medium text-green-900">Video Content</span>
                        <div className="text-right">
                          <div className="font-semibold text-green-600">+$250K</div>
                          <div className="text-xs text-green-700">Increase allocation</div>
                        </div>
                      </div>

                      <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                        <span className="font-medium text-red-900">Static Images</span>
                        <div className="text-right">
                          <div className="font-semibold text-red-600">-$150K</div>
                          <div className="text-xs text-red-700">Reduce allocation</div>
                        </div>
                      </div>

                      <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                        <span className="font-medium text-blue-900">Carousel Ads</span>
                        <div className="text-right">
                          <div className="font-semibold text-blue-600">+$100K</div>
                          <div className="text-xs text-blue-700">Scale successful format</div>
                        </div>
                      </div>
                    </div>

                    <div className="p-3 bg-gray-50 rounded-lg mt-4">
                      <div className="font-medium text-gray-900 mb-2">Expected Impact:</div>
                      <div className="text-sm space-y-1">
                        <div className="flex justify-between">
                          <span>ROAS Improvement:</span>
                          <span className="font-semibold text-green-600">+22%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Additional Revenue:</span>
                          <span className="font-semibold text-green-600">+$1.8M</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* A/B Testing Recommendations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <RefreshCw className="h-5 w-5 mr-2 text-purple-600" />
                  A/B Testing & Experimentation Pipeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 border rounded-lg">
                      <div className="font-medium text-gray-900 mb-2">Creative Format Test</div>
                      <div className="text-sm text-gray-600 mb-3">
                        Video vs Static vs Carousel for summer campaign
                      </div>
                      <div className="flex items-center justify-between">
                        <Badge className="bg-yellow-100 text-yellow-800">In Progress</Badge>
                        <span className="text-xs text-gray-500">Day 5/14</span>
                      </div>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <div className="font-medium text-gray-900 mb-2">CTA Optimization</div>
                      <div className="text-sm text-gray-600 mb-3">
                        "Shop Now" vs "Learn More" vs "Get Offer"
                      </div>
                      <div className="flex items-center justify-between">
                        <Badge className="bg-green-100 text-green-800">Completed</Badge>
                        <span className="text-xs text-gray-500">Winner: "Get Offer"</span>
                      </div>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <div className="font-medium text-gray-900 mb-2">Color Psychology</div>
                      <div className="text-sm text-gray-600 mb-3">
                        Blue vs Green vs Orange primary colors
                      </div>
                      <div className="flex items-center justify-between">
                        <Badge className="bg-blue-100 text-blue-800">Planned</Badge>
                        <span className="text-xs text-gray-500">Starts next week</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Export and Integration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Download className="h-5 w-5 mr-2 text-green-600" />
                  Data Export & BI System Integration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Export Options</h4>
                    <div className="space-y-2">
                      <Button variant="outline" className="w-full justify-start">
                        <Download className="h-4 w-4 mr-2" />
                        Creative Performance Report (.xlsx)
                      </Button>
                      <Button variant="outline" className="w-full justify-start">
                        <FileText className="h-4 w-4 mr-2" />
                        Optimization Recommendations (.pdf)
                      </Button>
                      <Button variant="outline" className="w-full justify-start">
                        <BarChart3 className="h-4 w-4 mr-2" />
                        Raw Data Export (.csv)
                      </Button>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">BI Integration</h4>
                    <div className="space-y-2">
                      <Button variant="outline" className="w-full justify-start">
                        <Zap className="h-4 w-4 mr-2" />
                        Connect to Tableau
                      </Button>
                      <Button variant="outline" className="w-full justify-start">
                        <BarChart3 className="h-4 w-4 mr-2" />
                        PowerBI Dashboard
                      </Button>
                      <Button variant="outline" className="w-full justify-start">
                        <Target className="h-4 w-4 mr-2" />
                        Google Data Studio
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* AI Insights Chatbot */}
      <AIInsightsChatbot />
    </div>
  );
};

export default CreativeAudit;